"use client";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import InitialSurvey from "./InitialSurvey";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";

type SurveyScreen = "whatsapp" | "welcome" | "survey";

const WHATSAPP_COMMUNITY_LINK = import.meta.env.VITE_WHATSAPP_COMMUNITY_LINK || "";

const sharedBg = (
  <div
    className="fixed inset-0 z-0 pointer-events-none"
    style={{
      backgroundImage:
        "linear-gradient(rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.2) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
    }}
  />
);

export default function StartSurvey() {
  const location = useLocation();
  const { missingStepIds = [], isFirstTime = true } = (location.state || {}) as {
    missingStepIds?: string[];
    isFirstTime?: boolean;
  };

  // Always show WhatsApp community screen first, even for returning users
  const initialScreen: SurveyScreen = "whatsapp";
  const [screen, setScreen] = useState<SurveyScreen>(initialScreen);
  const { language } = useAuth();

  if (screen === "survey") {
    return <InitialSurvey missingStepIds={missingStepIds} />;
  }

  if (screen === "welcome") {
    return (
      <div className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto">
        {sharedBg}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="w-full max-w-2xl mx-auto">
            <h1 className="text-[#0E172A] font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-[58px] leading-[1.1] mb-6 sm:mb-8 text-center sm:text-left">
              {dictionary["survey-title"][language as Language]}
            </h1>
            <div className="flex justify-center sm:justify-start">
              <button
                onClick={() => setScreen("survey")}
                className="w-full sm:w-auto bg-[#0E172A] hover:bg-[#0E172A]/80 active:bg-[#0E172A]/70 text-white text-base sm:text-lg md:text-xl lg:text-2xl font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <span>{dictionary["start-survey"][language as Language]}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // WhatsApp community screen
  return (
    <div className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto">
      {sharedBg}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-12 md:py-16 lg:py-20">
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-6 sm:gap-8">
          <p className="text-[#0E172A] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-[1.2] text-center whitespace-pre-line">
            {dictionary["whatsapp-community-message"][language as Language]}
          </p>

          <div className="flex flex-col items-center gap-3 sm:gap-4">
            {/* WhatsApp join button */}
            <a
              href={WHATSAPP_COMMUNITY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#1ebe5d] active:bg-[#18a852] text-white text-base sm:text-lg md:text-xl font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center gap-3 shadow-sm"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/5/5e/WhatsApp_icon.png"
                alt="WhatsApp"
                className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0"
              />
              <span>{dictionary["whatsapp-community-join"][language as Language]}</span>
            </a>

            {/* Already member button */}
            <button
              onClick={() => setScreen(isFirstTime ? "welcome" : "survey")}
              className="bg-[#0E172A] hover:bg-[#0E172A]/80 active:bg-[#0E172A]/70 text-white text-base sm:text-lg md:text-xl font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95"
            >
              {dictionary["whatsapp-community-already-member"][language as Language]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
